/**
 * Galería PDP Optima: swipe en móvil, feedback en miniaturas y plegables de información.
 */

const OPTIMA_PDP_MOBILE_MQ = '(max-width: 749px)';

/** @type {WeakMap<Element, Element[]>} */
const foldRestoreMap = new WeakMap();

function initOptimaPdpGallery() {
  const gallery = document.querySelector('.product-information media-gallery');
  if (!gallery) return;

  const slideshow = gallery.querySelector('slideshow-component');
  const thumbs = gallery.querySelectorAll('.slideshow-controls__thumbnail');

  for (const thumb of thumbs) {
    thumb.addEventListener(
      'click',
      () => {
        thumb.classList.add('optima-thumb--pulse');
        window.setTimeout(() => thumb.classList.remove('optima-thumb--pulse'), 320);
      },
      { passive: true }
    );
  }

  if (!slideshow || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let touchStartX = 0;
  gallery.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
    },
    { passive: true }
  );

  gallery.addEventListener(
    'touchend',
    (e) => {
      const zoomed = gallery.querySelector('.optima-zoom-host.is-pinch-zoomed');
      if (zoomed) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) < 48) return;
      const prev = gallery.querySelector('[name="previous"], .slideshow-control--previous');
      const next = gallery.querySelector('[name="next"], .slideshow-control--next');
      if (dx > 0 && prev instanceof HTMLElement) prev.click();
      if (dx < 0 && next instanceof HTMLElement) next.click();
    },
    { passive: true }
  );
}

function getHeaderInfoGroup() {
  return document.querySelector(
    '.product-details > .group-block > .group-block-content > .group-block > .group-block-content'
  );
}

function collectHeaderFoldables(headerContent) {
  /** @type {Element[]} */
  const foldables = [];

  for (const child of headerContent.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (!child.classList.contains('text-block')) continue;
    if (child.querySelector('h1')) continue;
    if (child.tagName === 'PRODUCT-PRICE' || child.querySelector('product-price')) continue;
    if (child.querySelector('optima-product-conversion')) continue;
    if (child.classList.contains('text-block--social_proof_optima')) continue;
    foldables.push(child);
  }

  return foldables;
}

function unwrapMobileAboutFold() {
  const existing = document.querySelector('details[data-optima-auto-fold="about"]');
  if (!existing) return;

  const headerContent = getHeaderInfoGroup();
  const stored = headerContent ? foldRestoreMap.get(headerContent) : undefined;
  const body = existing.querySelector('[data-optima-fold-body]');

  if (headerContent && body) {
    while (body.firstChild) {
      headerContent.appendChild(body.firstChild);
    }
  }

  if (stored) {
    for (const node of stored) {
      if (node.isConnected) headerContent?.appendChild(node);
    }
  }

  existing.remove();
  if (headerContent) {
    delete headerContent.dataset.optimaFoldReady;
    foldRestoreMap.delete(headerContent);
  }
}

function wrapMobileAboutFold() {
  if (!window.matchMedia(OPTIMA_PDP_MOBILE_MQ).matches) {
    unwrapMobileAboutFold();
    return;
  }

  const headerContent = getHeaderInfoGroup();
  if (!headerContent || headerContent.dataset.optimaFoldReady === 'true') return;

  const foldables = collectHeaderFoldables(headerContent);
  if (foldables.length === 0) return;

  foldRestoreMap.set(headerContent, [...foldables]);

  const details = document.createElement('details');
  details.className = 'optima-pdp-fold optima-pdp-fold--about';
  details.dataset.optimaAutoFold = 'about';

  const summary = document.createElement('summary');
  summary.textContent =
    document.documentElement.lang?.startsWith('es') ? 'Reseña y beneficios' : 'Overview & benefits';

  const body = document.createElement('div');
  body.dataset.optimaFoldBody = '';

  for (const node of foldables) {
    body.appendChild(node);
  }

  details.append(summary, body);

  const savings = headerContent.querySelector('optima-product-conversion');
  if (savings instanceof HTMLElement) {
    savings.after(details);
  } else {
    const price = headerContent.querySelector('product-price');
    if (price instanceof HTMLElement) {
      price.after(details);
    } else {
      headerContent.appendChild(details);
    }
  }

  headerContent.dataset.optimaFoldReady = 'true';
}

function wrapMobilePaymentFold() {
  if (!window.matchMedia(OPTIMA_PDP_MOBILE_MQ).matches) return;

  const buyBlock = document.querySelector('.product-details .buy-buttons-block');
  if (!buyBlock || buyBlock.dataset.optimaPaymentFold === 'true') return;

  const payment = buyBlock.querySelector('.accelerated-checkout-block');
  if (!payment) return;

  const details = document.createElement('details');
  details.className = 'optima-pdp-fold optima-pdp-fold--payments';

  const summary = document.createElement('summary');
  summary.textContent =
    document.documentElement.lang?.startsWith('es') ? 'Más formas de pago' : 'More payment options';

  const body = document.createElement('div');
  body.className = 'optima-pdp-fold__payments-body';
  body.appendChild(payment);

  details.append(summary, body);
  buyBlock.appendChild(details);
  buyBlock.dataset.optimaPaymentFold = 'true';
}

function initOptimaPdpMobileFolds() {
  wrapMobileAboutFold();
  wrapMobilePaymentFold();
}

function setProductQuantity(qty) {
  const root = document.querySelector('.product-details');
  if (!root) return;

  const input =
    root.querySelector('quantity-selector input[type="number"]') ||
    root.querySelector('input[name="quantity"]');
  if (!(input instanceof HTMLInputElement)) return;

  input.value = String(qty);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function initOptimaQuantityPricing() {
  document.querySelectorAll('optima-quantity-pricing').forEach((root) => {
    if (root.dataset.optimaQtyBound === 'true') return;
    root.dataset.optimaQtyBound = 'true';

    const tiers = root.querySelectorAll('[data-optima-qty]');
    const applySelection = (tier) => {
      const qty = tier.getAttribute('data-optima-qty');
      if (!qty) return;

      tiers.forEach((t) => {
        const selected = t === tier;
        t.classList.toggle('is-selected', selected);
        t.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const quantity = Number.parseInt(qty, 10);
      setProductQuantity(quantity);
      document.dispatchEvent(
        new CustomEvent('optima:quantity-tier-selected', { bubbles: true, detail: { quantity } }),
      );
    };

    tiers.forEach((tier) => {
      tier.addEventListener('click', () => applySelection(tier));
    });

    const initial = root.querySelector('[data-optima-qty].is-selected') || tiers[0];
    if (initial instanceof HTMLElement) {
      applySelection(initial);
    }
  });
}

function initOptimaPdp() {
  initOptimaPdpGallery();
  initOptimaPdpMobileFolds();
  initOptimaQuantityPricing();

  const mq = window.matchMedia(OPTIMA_PDP_MOBILE_MQ);
  mq.addEventListener('change', () => {
    unwrapMobileAboutFold();
    initOptimaPdpMobileFolds();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOptimaPdp, { once: true });
} else {
  initOptimaPdp();
}

document.addEventListener('shopify:section:load', () => {
  unwrapMobileAboutFold();
  const buyBlock = document.querySelector('.product-details .buy-buttons-block');
  if (buyBlock) delete buyBlock.dataset.optimaPaymentFold;
  initOptimaPdp();
  initOptimaQuantityPricing();
});
